import { ComponentWithTooltip } from 'components/ComponentWithTooltip';
import { ConfirmationDialog } from 'components/ConfirmationDialog';
import type { PriceListUerRowInput, ChapterEnum } from 'gql/graphql';
import { WorkOrderStatusEnum } from 'gql/graphql';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';
import { AddWorkOrderPriceForm } from 'pages/Project/Common/AddWorkOrderPriceForm';
import { DEFAULT_ADD_UER_STEPS, type StepsContent } from 'pages/Project/Common/AddWorkOrderPriceForm/helpersAndTypes';
import { CommonValueNoteCellRenderer } from 'pages/Project/Common/AddWorkOrderPriceForm/ValueNoteCellRenderer';
import { CreateOrEditUer } from 'pages/Project/UerProject/Uer/CreateOrEditUer';
import { Button, cnMixSpace } from 'uikit';

import type { ColDef } from 'ag-grid-community';
import type { ElementRef } from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';

import { useWorkOrderObjects } from '../hooks/useWorkOrderObjects';

import type { IAddWorkOrderUerProps, IObjectTabFilters } from './helpersAndTypes';
import { ADD_UER_STEPS } from './helpersAndTypes';
import { useAddPricesToWorkOrder } from './hooks';
import { usePricesTableRows } from './hooks/usePricesTableRows';

export const AddWorkOrderUer = (props: IAddWorkOrderUerProps) => {
    const {
        onClose,
        constructionTypeId,
        workOrderId,
        contractId,
        reloadWorkOrderTable,
        objectId,
        subObjectId,
        workOrderStatus,
        catalogVersion,
        catalogName,
    } = props;
    const controlRef = useRef<ElementRef<typeof AddWorkOrderPriceForm>>(null);

    const [selectedChapter, setSelectedChapter] = useState<ChapterEnum | undefined>(undefined);
    const [selectedLeafObjects, setSelectedLeafObjects] = useState<number[] | undefined>(undefined);
    const [selectedRowBaseRowIds, setSelectedRowBaseRowIds] = useState<number[] | undefined>(undefined);
    const [valuesNotes, setValuesNotes] = useState<Record<number, string>>({});

    const { workOrderObjects } = useWorkOrderObjects(workOrderId);

    const isNextStepDisabled = useMemo(
        () => !(selectedChapter && Object.values(selectedLeafObjects ?? {})?.some((checked) => checked)),
        [selectedChapter, selectedLeafObjects],
    );

    const {
        dataSource,
        onSearchStringClear,
        searchString,
        onSearch,
        onSearchStringChange,
        filters,
        changeFilterHandler,
    } = usePricesTableRows({
        workOrderId,
        workOrderObjectIds: selectedLeafObjects as any,
        chapter: selectedChapter,
        constructionTypeId,
    });

    const checkedDataSender = useCallback(
        ({
            objectTabFilters,
            checkedLeafObjects,
            uerRowBaseRowIds,
        }: {
            checkedLeafObjects: number[];
            uerRowBaseRowIds: number[];
            objectTabFilters: IObjectTabFilters;
        }) => {
            setSelectedChapter(objectTabFilters?.chapter?.id);
            setSelectedLeafObjects(checkedLeafObjects);
            setSelectedRowBaseRowIds(uerRowBaseRowIds);
        },
        [],
    );

    const uerInputs = useMemo<PriceListUerRowInput[] | undefined>(
        () =>
            selectedRowBaseRowIds?.map((id) => ({
                uerBaseRowId: id,
                rationale: valuesNotes?.[id],
            })),
        [selectedRowBaseRowIds, valuesNotes],
    );

    const { addPricesToWorkOrderHandler, isLoading } = useAddPricesToWorkOrder({
        chapter: selectedChapter,
        workOrderObjectIds: selectedLeafObjects as any,
        workOrderId,
        constructionTypeId,
        uerInputs,
        reloadWorkOrderTable,
        onClose,
    });

    const firstStepContent = useCallback(
        (onStepClickCreator: (step: (typeof DEFAULT_ADD_UER_STEPS)[number]) => () => void) => (
            <Button
                label="Далее"
                onClick={onStepClickCreator(DEFAULT_ADD_UER_STEPS[1])}
                disabled={isNextStepDisabled}
                size="m"
            />
        ),
        [isNextStepDisabled],
    );

    const lastStepContent = useCallback(
        (onStepClickCreator: (step: (typeof DEFAULT_ADD_UER_STEPS)[number]) => () => void) => {
            const addBtnLabel =
                workOrderStatus === WorkOrderStatusEnum.InProgress ? 'Добавить в Акт' : 'Добавить в Наряд-Заказ';

            return (
                <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
                    <CreateOrEditUer
                        constructionTypeId={constructionTypeId}
                        onSave={() => undefined}
                        contractId={contractId}
                        chapter={selectedChapter}
                        setCheckedIds={controlRef?.current?.setCheckedPrices}
                        resetPageData={controlRef?.current?.refreshAddDataTable}
                        fromWorkOrder
                        catalogVersion={catalogVersion}
                        catalogName={catalogName}
                    />

                    <div>
                        <Button
                            size="s"
                            label={addBtnLabel}
                            onClick={addPricesToWorkOrderHandler}
                            disabled={false}
                            className={cnMixSpace({
                                mR: 'm',
                            })}
                        />
                        <Button size="s" view="ghost" label="Назад" onClick={onStepClickCreator(ADD_UER_STEPS[0])} />
                    </div>
                </div>
            );
        },
        [
            addPricesToWorkOrderHandler,
            catalogName,
            catalogVersion,
            constructionTypeId,
            contractId,
            selectedChapter,
            workOrderStatus,
        ],
    );

    const stepsContent = useMemo<StepsContent>(
        () => ({
            0: firstStepContent,
            1: lastStepContent,
        }),
        [lastStepContent, firstStepContent],
    );
    const getObjSelectItemDisabled = useCallback(
        (item: { id?: number }) => {
            const itemForCheck = workOrderObjects?.find((o) => o?.id === item?.id);

            if (workOrderStatus === WorkOrderStatusEnum.InProgress) {
                return !itemForCheck?.hasActsKC2;
            }

            return false;
        },
        [workOrderObjects, workOrderStatus],
    );
    const getObjItemLabel = useCallback(
        (item: { id?: number; label?: string }) => {
            const itemForCheck = workOrderObjects?.find((o) => o?.id === item?.id);

            if (workOrderStatus === WorkOrderStatusEnum.InProgress && !itemForCheck?.hasActsKC2) {
                return (
                    <ComponentWithTooltip
                        tooltipText="Для выбора объекта строительства необходимо создать Акт&nbsp;КС-2 в одном из статусов: «В&nbsp;работе» или «На&nbsp;проверке»"
                        direction="rightCenter"
                        size="l"
                        styles={{
                            width: '100%',
                        }}
                    >
                        {item?.label ?? ''}
                    </ComponentWithTooltip>
                );
            }

            return item?.label ?? '';
        },
        [workOrderObjects, workOrderStatus],
    );

    const initiallyCheckedObj = useMemo(() => {
        if (objectId) {
            const object = workOrderObjects?.find((o) => o?.id === objectId);

            if (
                (object && workOrderStatus !== WorkOrderStatusEnum.InProgress) ||
                (object && workOrderStatus === WorkOrderStatusEnum.InProgress && Boolean(object?.hasActsKC2))
            ) {
                return object;
            }
        }

        return undefined;
    }, [objectId, workOrderObjects, workOrderStatus]);

    const initiallyChecked = useMemo(() => {
        if (objectId || subObjectId) {
            const objHasActs = workOrderObjects?.find((o) => o?.id === objectId)?.hasActsKC2;

            if (
                workOrderStatus !== WorkOrderStatusEnum.InProgress ||
                (workOrderStatus === WorkOrderStatusEnum.InProgress && objHasActs)
            ) {
                if (subObjectId) {
                    return {
                        [subObjectId]: true,
                    };
                }

                return {
                    ...(objectId && { [objectId]: true }),
                };
            }
        }

        return undefined;
    }, [objectId, subObjectId, workOrderObjects, workOrderStatus]);

    const filterVariables = useMemo(
        () => ({
            workOrderId,
            constructionTypeId,
            workOrderObjectIds: selectedLeafObjects,
            chapter: selectedChapter,
        }),
        [constructionTypeId, selectedChapter, selectedLeafObjects, workOrderId],
    );

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: onClose,
        });

    const handleClose = useCallback(() => {
        const isDirty = Boolean(selectedChapter && selectedLeafObjects?.length);

        if (isDirty) {
            handleOpenConfirmationDialog();
        } else {
            onClose();
        }
    }, [handleOpenConfirmationDialog, onClose, selectedChapter, selectedLeafObjects?.length]);

    const extraColumn = useMemo<ColDef[]>(
        () =>
            workOrderStatus === WorkOrderStatusEnum.InProgress
                ? [
                      {
                          headerName: 'Примечание',
                          field: 'valueNote',
                          flex: 1,
                          cellRenderer: CommonValueNoteCellRenderer,
                          cellRendererParams: {
                              valuesNotes,
                              setValuesNotes,
                              checkedIds: selectedRowBaseRowIds,
                          },
                      },
                  ]
                : [],
        [selectedRowBaseRowIds, valuesNotes, workOrderStatus],
    );

    return (
        <>
            <AddWorkOrderPriceForm
                ref={controlRef}
                onClose={handleClose}
                objects={workOrderObjects as any}
                isLoading={isLoading}
                constructionTypeId={constructionTypeId}
                stepsContent={stepsContent}
                onSearchStringClear={onSearchStringClear}
                searchString={searchString}
                dataSource={dataSource}
                onSearchStringChange={onSearchStringChange}
                filters={filters}
                onSearch={onSearch}
                checkedDataSender={checkedDataSender}
                initiallyCheckedLeafs={initiallyChecked}
                initiallyCheckedObj={initiallyCheckedObj as any}
                getObjItemDisabled={getObjSelectItemDisabled}
                getObjItemLabel={getObjItemLabel}
                filterVariables={filterVariables}
                changeFilterHandler={changeFilterHandler}
                extraColumnDefs={extraColumn}
            />
            <ConfirmationDialog
                textHeader="Отмена действия"
                textConfirmation="Вы уверены, что хотите отменить? Заполненные данные не будут сохранены"
                confirmButtonText="Отменить"
                cancelButtonText="Не отменять"
                isOpened={isOpenConfirmationDialog}
                onSubmit={handleActionQuery}
                onClose={handleCloseConfirmationDialog}
            />
        </>
    );
};

AddWorkOrderUer.displayName = 'AddWorkOrderUer';
